import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Heart, Globe, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { Producer } from "@/lib/producerTypes";
import { SocialLinks } from "./SocialLinks";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ProducerHeaderProps {
  producer: Producer;
  onFollowerCountUpdate?: (newCount: number) => void;
}

export const ProducerHeader = ({ producer, onFollowerCountUpdate }: ProducerHeaderProps) => {
  const [showFullBio, setShowFullBio] = useState(false);
  const [localFollowerCount, setLocalFollowerCount] = useState(producer.followerCount || 0);
  const [isFollowing, setIsFollowing] = useState(false);
  const { toast } = useToast();
  
  const shouldTruncateBio = producer.bio && producer.bio.length > 300;
  const displayBio = shouldTruncateBio && !showFullBio 
    ? producer.bio.slice(0, 300) + "..." 
    : producer.bio;

  const handleFollow = async () => {
    if (isFollowing) return;
    
    setIsFollowing(true);
    
    try {
      // Update the follower count in the database
      const newCount = localFollowerCount + 1;
      console.log('Updating follower count for producer ID:', producer.id, 'to:', newCount);
      
      const { data, error } = await supabase
        .from('producers_bio')
        .update({ follower_count: newCount })
        .eq('id', producer.id)
        .select();

      console.log('Database update result:', { data, error });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // Update local state
      setLocalFollowerCount(newCount);
      onFollowerCountUpdate?.(newCount);
      
    } catch (error) {
      console.error('Error following producer:', error);
      toast({
        title: "Oops! Something went wrong",
        description: "Please try again later",
        variant: "destructive",
      });
      setIsFollowing(false);
    }
  };

  return (
    <header className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600/20 via-pink-500/20 to-orange-400/20 p-8 md:p-12 backdrop-blur-sm border border-white/10">
      
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-purple-400/30 to-transparent rounded-full blur-xl" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-pink-400/30 to-transparent rounded-full blur-xl" />
      
      <div className="relative z-10">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          {/* Avatar section with creative shape */}
          <div className="relative group">
            <div className="w-24 h-24 md:w-32 md:h-32 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl rotate-6 group-hover:rotate-12 transition-transform duration-300" />
              <Avatar className="relative w-full h-full rounded-2xl border-4 border-white/20 shadow-2xl">
                <AvatarImage 
                  src={producer.avatarUrl} 
                  alt={producer.name}
                  className="object-cover"
                />
                <AvatarFallback className="rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 text-white text-xl font-bold">
                  {producer.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>

          {/* Main content */}
          <div className="flex-1 space-y-6">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div className="space-y-4">
                <div>
            <h1 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent leading-tight">
              {producer.title || producer.name}
            </h1>
                  {localFollowerCount > 0 && (
                    <div className="relative">
                      <p className="text-white/70 text-lg mt-2 transition-all duration-500">
                        <span className={`${isFollowing ? 'animate-pulse text-rainbow bg-gradient-to-r from-purple-400 via-pink-400 to-yellow-400 bg-clip-text text-transparent font-bold' : ''}`}>
                          {localFollowerCount.toLocaleString()}
                        </span> followers
                      </p>
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex gap-3 flex-wrap items-center">
                  <Button 
                    variant="default" 
                    onClick={handleFollow}
                    disabled={isFollowing}
                    className={`${
                      isFollowing 
                        ? 'bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-500 hover:to-teal-600 shadow-cyan-400/30' 
                        : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
                    } text-white border-0 rounded-full px-6 shadow-lg hover:shadow-xl transition-all duration-500`}
                  >
                    <Heart className={`w-4 h-4 mr-2 ${isFollowing ? 'fill-current' : ''}`} />
                    {isFollowing ? 'Following ✨' : 'Follow'}
                  </Button>
                  
                  {producer.websiteUrl && (
                    <Button 
                      variant="outline" 
                      asChild
                      className="rounded-full bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm"
                    >
                      <a 
                        href={producer.websiteUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        aria-label={`Visit ${producer.name} website`}
                      >
                        <Globe className="w-4 h-4 mr-2" />
                        Website
                      </a>
                    </Button>
                  )}
                  
                  {/* Social links inline */}
                  <SocialLinks socials={producer.socials} />
                </div>
              </div>

              {/* Powered by badge */}
              <div className="flex items-center gap-2 opacity-60">
                <Badge variant="secondary" className="bg-white/10 text-white/80 border-white/20">
                  POWERED BY
                </Badge>
                <span className="text-white/60 font-semibold">SLAY</span>
              </div>
            </div>

            {/* Bio section */}
            {producer.bio && (
              <div className="bg-white/5 rounded-2xl p-6 backdrop-blur-sm border border-white/10">
                <p className="text-white/90 leading-relaxed text-lg">
                  {displayBio}
                </p>
                {shouldTruncateBio && (
                  <button
                    onClick={() => setShowFullBio(!showFullBio)}
                    className="mt-3 text-purple-300 hover:text-purple-200 transition-colors duration-200 flex items-center gap-1 text-sm font-medium"
                  >
                    {showFullBio ? (
                      <>
                        Show less <ChevronUp className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        Show more <ChevronDown className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};